import { Mutation } from '@apollo/client/react/components';
import React from 'react';
import styled from 'styled-components';
import { compose, withProps } from 'recompose';
import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import scheme, { resolveAlert } from '../../../graph/alerts';
import { sortDate, sortNumber, sortString } from '../../../util/sort';
import { Text, TruncatedText } from '@/se/components/typography';
import format from 'date-fns/format';
import EntityView from '@/se/components/entity/EntityView';
import get from 'lodash/get';
import pick from 'lodash/fp/pick';
import { Actions } from './SensorGrid';
import Form from '@/se/components/Form';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import { withLabel } from '@/se/components/Label';
import TextAreaInput from '@/se/components/inputs/TextAreaInput';
import { colorForValue, roundFixed, temperatureLabel } from './SensorList';
import AlertMobileListItem from './AlertMobileListItem';
import Button from '@/se/components/Button[DEPRECATED]';
import ExpandableText from '../../core/ExpandableText';
import { NamedRange } from '../../core/DatePicker';
import Row from '@/se/components/collection/Row';
import { SeparatorListRowView } from '../../entities/patient/views/PatientListView';
import InTestingComponent from '../../InTestingComponent';

const idColumn = {
  title: '#',
  lens: item => item.id,
  sortFunction: (l, r) => sortString(l.name, r.name),
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
};

export const sensorIdColumn = {
  title: 'Sensor Id',
  lens: item => item.sensorId,
  sortFunction: (l, r) => sortString(l.sensorId, r.sensorId),
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
};

const timeColumn = {
  title: 'Time',
  lens: item => item,
  Component: ({ data }) => (
    <TruncatedText>{data.createdAt ? format(data.createdAt, 'MM/DD/YYYY HH:mm') : 'N/A'}</TruncatedText>
  ),
  sortFunction: (l, r) => sortDate(l.createdAt, r.createdAt),
};

const resolvedAtColumn = {
  title: 'Resolved At',
  lens: item => item,
  Component: ({ data }) => (
    <TruncatedText>{data.resolvedAt ? format(data.resolvedAt, 'MM/DD/YYYY HH:mm') : 'N/A'}</TruncatedText>
  ),
  sortFunction: (l, r) => sortDate(l.resolvedAt, r.resolvedAt),
};

const locationColumn = {
  title: 'Location',
  lens: item => get(item, 'sensor.location'),
  sortFunction: (l, r) => sortString(get(l, 'sensor.location'), get(r, 'sensor.location')),
  Component: props => <TruncatedText>{props.data}</TruncatedText>,
};

export const temperatureColumn = {
  title: 'Temp',
  lens: data => ({
    value: get(data, 'temperature.value'),
    unit: get(data, 'temperature.unit'),
    min: get(data, 'sensor.temperatureMin.value'),
    max: get(data, 'sensor.temperatureMax.value'),
  }),
  sortFunction: (l, r) => sortNumber(get(l, 'temperature.value'), get(r, 'temperature.value')),
  Component: ({ data: { value, unit, min, max } }) => (
    <Text style={{ color: colorForValue(value, min, max) }}>{`${roundFixed(value)} ${temperatureLabel(unit)}`}</Text>
  ),
};

const humidityColumn = {
  title: 'Humidity',
  lens: data => ({ value: data.humidity, min: get(data, 'sensor.humidityMin'), max: get(data, 'sensor.humidityMax') }),
  sortFunction: (l, r) => sortNumber(l.humidity, r.humidity),
  Component: ({ data: { value, min, max } }) => (
    <Text style={{ color: colorForValue(value, min, max) }}>{value ? `${roundFixed(value)} %` : undefined}</Text>
  ),
};

const actionColumn = {
  title: 'Action',
  lens: item => item.action,
  sortFunction: (l, r) => sortString(l.action, r.action),
  style: { maxWidth: '40em', minWidth: '20em' },
  Component: props => (
    <ExpandableText
      text={props.data}
      onClick={e => {
        e.stopPropagation();
      }}
    />
  ),
};

export const alertStatus = {
  RESOLVED: 'RESOLVED',
  ACTIVE: 'ACTIVE',
};

const statusColumn = {
  title: 'Status',
  lens: item => (item.isResolved ? alertStatus.RESOLVED : alertStatus.ACTIVE),
  sortFunction: (l, r) => sortString(l.action, r.action),
  Component: props => (
    <TruncatedText style={{ color: props.data === alertStatus.RESOLVED ? 'green' : 'red' }}>{props.data}</TruncatedText>
  ),
};

const ResolveIssueInput = withProps({
  schema: {
    action: compose(withProps({ required: true, rows: 5 }), withLabel('Comment / Action'))(TextAreaInput),
  },
})(ObjectInput);

const Wrapper = styled.div`
  > * + * {
    margin-top: 1em;
  }
`;

const RecipientsSection = styled.div`
  margin-top: 1.5em;
  padding: 1em;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const RecipientsTitle = styled.h4`
  margin: 0 0 0.75em 0;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
`;

const RecipientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
`;

const RecipientItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
  padding: 0.5em;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const RecipientBadge = styled.span`
  padding: 0.15em 0.5em;
  border-radius: 3px;
  font-size: 0.75em;
  font-weight: 500;
  text-transform: uppercase;
  background: ${props => (props.type === 'email' ? '#2196f3' : '#4caf50')};
  color: white;
`;

const ChannelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5em;
`;

const DispatchTime = styled.span`
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.6);
`;

const NotificationRecipients = ({ recipients, alertCreatedAt }) => {
  if (!recipients || recipients.length === 0) return null;

  const emailRecipients = recipients.filter(r => r.notificationType === 'email');
  const smsRecipients = recipients.filter(r => r.notificationType === 'sms');
  const dispatchTimeFormatted = alertCreatedAt ? format(alertCreatedAt, 'MM/DD/YYYY HH:mm') : 'N/A';

  return (
    <RecipientsSection>
      <RecipientsTitle>Notification Recipients</RecipientsTitle>
      <RecipientsList>
        {emailRecipients.length > 0 && (
          <>
            <ChannelHeader>
              <Text style={{ fontWeight: 500 }}>Email Notifications:</Text>
              <DispatchTime>Dispatched: {dispatchTimeFormatted}</DispatchTime>
            </ChannelHeader>
            {emailRecipients.map(r => (
              <RecipientItem key={`email-${r.userId}`}>
                <RecipientBadge type="email">Email</RecipientBadge>
                <Text>{r.name}</Text>
                <Text style={{ opacity: 0.7 }}>{r.email}</Text>
              </RecipientItem>
            ))}
          </>
        )}
        {smsRecipients.length > 0 && (
          <>
            <ChannelHeader>
              <Text style={{ fontWeight: 500 }}>SMS Notifications:</Text>
              <DispatchTime>Dispatched: {dispatchTimeFormatted}</DispatchTime>
            </ChannelHeader>
            {smsRecipients.map(r => (
              <RecipientItem key={`sms-${r.userId}`}>
                <RecipientBadge type="sms">SMS</RecipientBadge>
                <Text>{r.name}</Text>
                <Text style={{ opacity: 0.7 }}>{r.phone || 'No phone configured'}</Text>
              </RecipientItem>
            ))}
          </>
        )}
        {emailRecipients.length === 0 && smsRecipients.length === 0 && (
          <Text style={{ opacity: 0.7, fontStyle: 'italic' }}>No notification recipients configured</Text>
        )}
      </RecipientsList>
    </RecipientsSection>
  );
};

const Footer = ({ busy, isDirty, isResolved }) =>
  isDirty || !isResolved ? (
    <Actions>
      <Button disabled={busy} primary>
        {isResolved ? 'Update Comment' : 'Resolve Issue'}
      </Button>
    </Actions>
  ) : null;

const AlertPage = props => {
  const onSubmit = mutation => async data => {
    await mutation({
      variables: { ...data },
      refetchQueries: [{ query: scheme.item, variables: { id: get(props, 'data.id') } }],
    });
  };

  const notificationRecipients = get(props, 'data.notificationRecipients');
  const alertCreatedAt = get(props, 'data.createdAt');

  return (
    <Wrapper>
      <EntityView {...props} />
      {/* Show notification recipients only for superadmins with testing mode enabled */}
      <InTestingComponent>
        <NotificationRecipients recipients={notificationRecipients} alertCreatedAt={alertCreatedAt} />
      </InTestingComponent>
      <Mutation
        mutation={resolveAlert}
        refetchQueries={[{ query: scheme.item, variables: { id: get(props, 'data.id') } }]}
      >
        {(mutate, { loading }) => (
          // TODO: Here is some example for how to use
          <Form
            label={''}
            autoFocus
            input={ResolveIssueInput}
            onSubmit={onSubmit(mutate)}
            busy={loading}
            Footer={withProps({ isResolved: get(props, 'data.isResolved') })(Footer)}
            withoutReset={true}
            initialValue={{
              id: get(props, 'data.id'),
              action: get(props, 'data.action'),
            }}
          />
        )}
      </Mutation>
    </Wrapper>
  );
};

export const sortByResolved = (a, b) => a.isResolved - b.isResolved;

const sensorListView = props => {
  const { item } = props;

  if (!item) {
    return null;
  }

  const Component = item.separator ? SeparatorListRowView : Row;

  return <Component {...props} />;
};

const mapListProps = props => {
  const { data } = props;

  const sortedSensorAlerts = data.sensorAlerts?.slice().sort(sortByResolved);

  const firstResolved = sortedSensorAlerts?.find(e => e.isResolved === true);

  if (!firstResolved) return props;

  const separatorIndex = sortedSensorAlerts?.indexOf(firstResolved);

  sortedSensorAlerts?.splice(separatorIndex, 0, { separator: true, label: 'Resolved Alerts' });

  return {
    ...props,
    data: {
      ...data,
      sensorAlerts: sortedSensorAlerts,
    },
  };
};

const baseColumns = [timeColumn, locationColumn, temperatureColumn, humidityColumn, statusColumn, resolvedAtColumn];

const listColumns = [idColumn, ...baseColumns, actionColumn];

const SensorAlerts = GraphQLCRUD({
  entityName: 'SensorAlert',
  List: {
    Empty: () => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '20em' }}>
        There are no issues for the selected period.
      </div>
    ),
    //separator logic
    containsSeparator: true,
    View: sensorListView,
    mapListProps,

    tableKey: 'SensorAlerts',
    columns: listColumns,
    MobileItemComponent: AlertMobileListItem,
    TitleAndActions: () => null,
    defaultFilterValues: {
      dateRange: NamedRange.lastSevenDays(),
    },
    pickFilter: pick([
      'name',
      'physician',
      'procedureType',
      'hospital',
      'status',
      'dateRange',
      'category',
      'speciality',
    ]),
    queryOptionsProvider: props => ({
      variables: {
        sensors: props.sensorIds,
        filter: props.parentFilter,
      },
    }),
  },
  Show: {
    View: AlertPage,
    titleProvider: data => get(data, 'sensor.location', data.id),
    columns: [idColumn, sensorIdColumn, ...baseColumns],
    hideTitle: true,
  },
  scheme,
});

export default SensorAlerts;
